import React from "react";

import cloneDeep from "lodash/cloneDeep";

import Table from "@cloudscape-design/components/table";
import Pagination from "@cloudscape-design/components/pagination";
import Header from "@cloudscape-design/components/header";
import Input from "@cloudscape-design/components/input";
import Select from "@cloudscape-design/components/select";
import Button from "@cloudscape-design/components/button";
import Grid from "@cloudscape-design/components/grid";
import Box from "@cloudscape-design/components/box";
import Link from "@cloudscape-design/components/link";
import Preferences from "@cloudscape-design/components/collection-preferences";
import Alert from "@cloudscape-design/components/alert";

/* Recoil */
import { useRecoilValue, useSetRecoilState } from "recoil";
import { configStore } from "../stores/config";
import { gameStore } from "../stores/games";
import SpaceBetween from "@cloudscape-design/components/space-between";
import { gamesAddModalStore } from "../stores/gamesAddModal";
import { gamesStatsModalStore } from "../stores/gamesStatsModal";
import { gamesEditModalStore } from "../stores/gamesEditModal";
import { userStore } from "../stores/access";
import { gamesAutoManageModalStore } from "../stores/gamesAutomanageModal";
/* ------- */

import ProgressiveImage from "react-progressive-graceful-image";
import Poster from "../assets/poster.png";
import { gamesMetaTagsStore } from "../stores/gamesTagsModal";
import { gamesAllEditModalStore } from "../stores/gamesAllEditModal";
import { gamesSweepstakesModalStore } from "../stores/gamesSweepstakesModal";
import GamesSectionGamesEditor from "./GamesSectionGamesEditor";

const getErrorText = (error, fallback) => {
	if (typeof error === "string") return error;
	if (typeof error?.text === "string") return error.text;
	if (typeof error?.message === "string") return error.message;
	return fallback;
};

const isInterfaceApiError = (error) =>
	Boolean(error) &&
	Object.prototype.hasOwnProperty.call(error, "text") &&
	Object.prototype.hasOwnProperty.call(error, "code");

const mergeBadgeDefinitions = (definitions, createdDefinitions) => {
	const definitionsById = new Map(definitions.map((definition) => [String(definition.id), definition]));
	createdDefinitions.forEach((definition) => {
		definitionsById.set(String(definition.id), { ...definition });
	});
	return [...definitionsById.values()];
};

const buildPresentationBadges = (assignments, definitions) => {
	const definitionsById = new Map(definitions.map((definition) => [String(definition.id), definition]));
	return assignments.flatMap((assignment) => {
		const definition = definitionsById.get(String(assignment.badgeId));
		if (!definition || definition.status !== "active") return [];
		const isRtp = definition.kind === "rtpUp";
		return [{
			badgeId: assignment.badgeId,
			position: assignment.position,
			name: definition.name,
			textColor: definition.textColor,
			backgroundColor: definition.backgroundColor,
			rtpValue: assignment.rtpValue,
			systemType: isRtp ? "rtpUp" : null,
		}];
	});
};

class Games extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			sectionGamesEditorOpen: false,
			availabilityLoading: true,
			availableGameIds: [],
			badgeStatus: "loading",
			badgeError: null,
			badgeDefinitions: [],
			persistedAssignmentsByGameId: {},
			availabilityError: "",
		};
		this.mounted = false;
		this.scrollPosition = 0;
		this.currentSearch = "";
		this.query = {};
		this.loadTimeout = false;
		window.location.search
			.substr(1)
			.split("&")
			.forEach((item) => {
				this.query[item.split("=")[0]] = item.split("=")[1];
			});
	}

	canManageSectionGames() {
		const showColumns = Array.isArray(this.props.user.showColumn) ? this.props.user.showColumn : [];
		return Number(this.props.user.level) >= 3 || showColumns.includes("manageSectionGames");
	}

	componentDidMount() {
		this.mounted = true;
		if (!this.canManageSectionGames()) {
			this.setState({
				availabilityLoading: false,
				availabilityError: "",
			});
			return;
		}
		this.props.Interface.listAvailableGameIds()
			.then(({ availableGameIds, badges }) => {
				if (!this.mounted) return;
				this.setState({
					availabilityLoading: false,
					availableGameIds,
					badgeStatus: badges.status,
					badgeError: badges.error,
					badgeDefinitions: badges.definitions,
					persistedAssignmentsByGameId: badges.persistedAssignmentsByGameId,
					availabilityError: "",
				});
			})
			.catch((error) => {
				if (!this.mounted) return;
				const message = getErrorText(error, "Couldn't load client game availability");
				this.setState({
					availabilityLoading: false,
					availableGameIds: [],
					badgeStatus: "error",
					badgeError: message,
					badgeDefinitions: [],
					persistedAssignmentsByGameId: {},
					availabilityError: message,
				});
				if (!isInterfaceApiError(error)) this.props.Interface.addNoty("Error", message);
			});
	}

	componentWillUnmount() {
		this.mounted = false;
	}

	handleCommittedBadgeUpdate = ({ badgeChanges, createdBadgeDefinitions }) => {
		const nextDefinitions = mergeBadgeDefinitions(this.state.badgeDefinitions, createdBadgeDefinitions);
		const nextPersistedAssignmentsByGameId = { ...this.state.persistedAssignmentsByGameId };
		const committedAssignmentsByGameId = new Map();

		badgeChanges.forEach(({ gameId, assignments }) => {
			const clonedAssignments = assignments.map((assignment) => ({ ...assignment }));
			nextPersistedAssignmentsByGameId[String(gameId)] = clonedAssignments;
			committedAssignmentsByGameId.set(String(gameId), clonedAssignments);
		});

		this.setState({
			badgeDefinitions: nextDefinitions,
			persistedAssignmentsByGameId: nextPersistedAssignmentsByGameId,
		});

		if (!committedAssignmentsByGameId.size) return;
		const data = this.props.game.data.map((game) => {
			const assignments = committedAssignmentsByGameId.get(String(game.gameID));
			return assignments
				? { ...game, badges: buildPresentationBadges(assignments, nextDefinitions) }
				: game;
		});
		this.props.setGame({ ...this.props.game, data });
	};

	loadGames() {
		let data = cloneDeep(this.props.game.data);

		data.forEach((item) => {
			if (
				(!this.props.game.currentStatus || (item.active === 1 && this.props.game.currentStatus === "enabled") || (item.active !== 1 && this.props.game.currentStatus === "disabled")) &&
				(!this.props.game.currentCategory || item.categoryID.toString().indexOf(this.props.game.currentCategory.toString()) !== -1) &&
				(!this.props.game.currentProvider || item.providerID === this.props.game.currentProvider) &&
				(!this.props.game.currentSearch.length || item.gameID.toString().indexOf(this.props.game.currentSearch) !== -1 || item.name.toUpperCase().indexOf(this.props.game.currentSearch.toUpperCase()) !== -1 || item.providerID.toString().indexOf(this.props.game.currentSearch) !== -1)
			)
				item.filtered = true;
			else item.filtered = false;
		});

		this.props.setGame({ ...this.props.game, data });

		setTimeout(() => {
			let total = Math.ceil(
				this.props.game.data.filter((item) => {
					return item.filtered;
				}).length / this.props.config.game.perPage,
			);
			this.props.setGame({ ...this.props.game, pagination: { current: 1, total } });
		}, 0);

		/*
         this.props.setGame({ ...this.props.game, data: [], loaded: false });

         const params = {
             provider: this.props.game.currentProvider,
             searchType: this.props.game.currentSearchType,
             direction: this.props.game.currentDirection
         };

         if (this.props.game.currentSearch.length)
             params.search = this.props.game.currentSearch;

         this.props.setGame({ ...this.props.game, loaded: true, currentSearch: this.currentSearch, data: this.props.game.data, pagination: { current: 1, total: Math.ceil(this.props.game.data.length / this.props.config.game.perPage) } });*/
	}

	compare(a, b) {
		if (this.props.game.sorting.sortingColumn.sortingField.indexOf(".") === -1) {
			if (a[this.props.game.sorting.sortingColumn.sortingField] < b[this.props.game.sorting.sortingColumn.sortingField]) {
				return this.props.game.sorting.isDescending ? 1 : -1;
			}

			if (a[this.props.game.sorting.sortingColumn.sortingField] > b[this.props.game.sorting.sortingColumn.sortingField]) {
				return this.props.game.sorting.isDescending ? -1 : 1;
			}
		} else {
			if (a[this.props.game.sorting.sortingColumn.sortingField.split(".")[0]][this.props.game.sorting.sortingColumn.sortingField.split(".")[1]] < b[this.props.game.sorting.sortingColumn.sortingField.split(".")[0]][this.props.game.sorting.sortingColumn.sortingField.split(".")[1]]) {
				return this.props.game.sorting.isDescending ? 1 : -1;
			}

			if (a[this.props.game.sorting.sortingColumn.sortingField.split(".")[0]][this.props.game.sorting.sortingColumn.sortingField.split(".")[1]] > b[this.props.game.sorting.sortingColumn.sortingField.split(".")[0]][this.props.game.sorting.sortingColumn.sortingField.split(".")[1]]) {
				return this.props.game.sorting.isDescending ? -1 : 1;
			}
		}

		return 0;
	}

	handleKeyPress = (event) => {
		if (event.detail.key === "Enter")
			setTimeout(() => {
				this.loadGames();
			}, 0);
	};

	getEconomicScoreColor(score) {
		return score < 0 ? "red" : score < 5 ? "orange" : score < 50 ? "purple" : "green";
	}

	getVisibleColumns() {
		let columns = [];
		columns.push("poster");
		columns.push("bigPoster");
		columns.push("gameID");
		columns.push("name");
		columns.push("provider");
		columns.push("category");
		columns.push("status");
		columns.push("date");
		columns.push("integrator");
		columns.push("biases.bias");
		columns.push("tags");

		if (this.props.user.level >= 6) {
			columns.push("betsTotal");
			columns.push("betsCount");
			columns.push("winsTotal");
			columns.push("winsCount");
			columns.push("rtp");
		}

		return columns;
	}

	render() {
		const now = new Date();
		const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
		const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
		const canManageSectionGames = this.canManageSectionGames();
		const canManageGames = this.props.user.level >= 3 || this.props.user.showColumn.includes("autoManage");
		const availabilityReady = !this.state.availabilityLoading && !this.state.availabilityError;

		const isBiasActive = (from, to) => {
			const fromDate = from ? new Date(new Date(from).getFullYear(), new Date(from).getMonth(), new Date(from).getDate(), 0, 0, 0, 0) : null;
			const toDate = to ? new Date(new Date(to).getFullYear(), new Date(to).getMonth(), new Date(to).getDate(), 23, 59, 59, 999) : null;

			return (!from && !to) || (from && !to && fromDate <= startOfDay) || (!from && to && toDate >= endOfDay) || (from && to && fromDate <= startOfDay && toDate >= endOfDay);
		};

		return (
			<>
				{this.state.sectionGamesEditorOpen ? (
					<GamesSectionGamesEditor
						Interface={this.props.Interface}
						canImportSection={canManageSectionGames}
						gameStore={this.props.game}
						availableGameIds={this.state.availableGameIds}
						badgeBootstrap={{
							status: this.state.badgeStatus,
							error: this.state.badgeError,
							definitions: this.state.badgeDefinitions,
							persistedAssignmentsByGameId: this.state.persistedAssignmentsByGameId,
						}}
						onBadgesCommitted={this.handleCommittedBadgeUpdate}
						onClose={() => this.setState({ sectionGamesEditorOpen: false })}
					/>
				) : null}
				{this.props.game.loaded && this.props.game.error.text.length ? (
					<Box
						margin={{ bottom: "m" }}
						children={
							<Alert
								type="error"
								header="Error loading data:"
								children={
									<>
										<Box variant="p" children={this.props.game.error.text} />
										<hr />
										<Box
											variant="p"
											children={
												<>
													request error code: {this.props.game.error.code} | request timestamp: {this.props.game.error.timestamp}
												</>
											}
										/>
									</>
								}
							/>
						}
					/>
				) : (
					<></>
				)}

				<Table
					onSelectionChange={(selection) => {
						this.props.setGamesAllEditModal({ ...this.props.gamesAllEditModal, selectedItems: selection.detail.selectedItems });
					}}
					selectionType="multi"
					selectedItems={this.props.gamesAllEditModal.selectedItems}
					visibleColumns={this.getVisibleColumns()}
					stickyHeader="true"
					loading={!this.props.game.loaded}
					sortingColumn={{ sortingField: this.props.game.sorting.sortingColumn.sortingField }}
					sortingDescending={this.props.game.sorting.isDescending}
					onSortingChange={(sorting) => {
						this.props.setGame({ ...this.props.game, sorting: sorting.detail });
						setTimeout(() => {
							this.props.setGame({
								...this.props.game,
								data: cloneDeep(this.props.game.data).sort((a, b) => {
									return this.compare(a, b);
								}),
							});
						}, 0);
					}}
					columnDefinitions={[
						{
							width: 70,
							id: "bigPoster",
							header: "Poster",
							cell: (item) => (
								<ProgressiveImage src={item.bigPoster} placeholder={Poster}>
									{(src) => <img style={{ width: "70px" }} src={src} alt={item.name} />}
								</ProgressiveImage>
							),
						},
						{
							id: "gameID",
							header: "Game ID",
							sortingField: "gameID",
							cell: (item) => (
								<Button
									onClick={() => {
										this.props.setGamesEditModal({ ...this.props.gamesEditModal, editAllGames: false, biases: item.biases, show: true, loaded: false, providerID: item.providerID, id: item.gameID, editID: item.gameID, tagIds: item.tags.filter((tag) => Number(tag)) });
									}}
									iconName="">
									{item.gameID}
								</Button>
							),
						},
						{ id: "name", header: "Name", sortingField: "name", cell: (item) => item.name },
						{ id: "provider", header: "Provider", sortingField: "providerID", cell: (item) => this.props.Interface.getProviderByID(item.providerID) },
						{ id: "integrator", header: "Integrator", sortingField: "integrator", cell: (item) => this.props.Interface.getProviderIntegratorByID(item.providerID) },
						{
							id: "category",
							header: "Category",
							cell: (item) =>
								item.categoryID
									.toString()
									.split(",")
									.map((category, i) => {
										return (
											<>
												{this.props.Interface.getCategoryByID(category)}
												{i + 1 < item.categoryID.toString().split(",").length ? ", " : ""}
											</>
										);
									}),
						},
						{ id: "betsTotal", header: "Bets total", sortingField: "bets.total", cell: (item) => <>{item.bets.total} RUB.</> },
						{ id: "betsCount", header: "Bets count", sortingField: "bets.count", cell: (item) => item.bets.count },
						{ id: "winsTotal", header: "Wins total", sortingField: "wins.total", cell: (item) => <>{item.wins.total} RUB.</> },
						{
							id: "rtp",
							header: "RTP",
							sortingField: "rtp",
							cell: (item) => (
								<Link
									onFollow={() => {
										this.props.setGamesStatsModal({ ...this.props.gamesStatsModal, show: 1, needLoad: true, gameID: item.gameID });
									}}>
									{item.rtp}%
								</Link>
							),
						},
						{ id: "status", header: "Status", sortingField: "active", cell: (item) => <>{item.active === 1 ? "Enabled" : item.active === 0 ? "Disabled" : "Undefined"} </> },
						{ id: "date", header: "Date", sortingField: "date", cell: (item) => new Date(item.date).toLocaleDateString() },
						{
							id: "biases.bias",
							header: "Bias",
							sortingField: "biases.bias",
							cell: (item) => (
								<Box
									key={item.id}
									variant="span"
									color={isBiasActive(item.biases.from, item.biases.to) ? "text-status-success" : "text-status-error"}
									children={
										<>
											{item.biases.bias}
											<br></br>({item.biases.from && item.biases.to ? `${item.biases.from.split("T")[0]} - ${item.biases.to.split("T")[0]}` : item.biases.from ? `${item.biases.from.split("T")[0]} - ...` : item.biases.to ? `... - ${item.biases.to.split("T")[0]}` : "..."})
										</>
									}
								/>
							),
						},
					]}
					items={this.props.game.data
						.filter((item) => {
							return item.filtered;
						})
						.slice((this.props.game.pagination.current - 1) * this.props.config.game.perPage, this.props.game.pagination.current * this.props.config.game.perPage)}
					pagination={
						<Pagination
							disabled={!this.props.game.loaded}
							currentPageIndex={this.props.game.pagination.current}
							pagesCount={this.props.game.pagination.total}
							onChange={(page) => {
								this.props.setGame({ ...this.props.game, pagination: { ...this.props.game.pagination, current: page.detail.currentPageIndex } });
							}}
						/>
					}
					preferences={
						<Preferences
							cancelLabel="Cancel"
							confirmLabel="Confirm"
							pageSizePreference={{ title: "Games per page", options: this.props.config.game.preferences.perPage }}
							preferences={{ pageSize: this.props.config.game.perPage }}
							title="Preferences"
							onConfirm={(preferences) => {
								localStorage.setItem("games.perPage", preferences.detail.pageSize);
								this.props.setConfig({ ...this.props.config, game: { ...this.props.config.game, perPage: preferences.detail.pageSize } });
								this.props.setGame({ ...this.props.game, pagination: { current: 1, total: Math.ceil(this.props.game.data.length / preferences.detail.pageSize) } });
							}}
						/>
					}
					filter={
						<Box
							margin={{ bottom: "m" }}
							children={
								<Grid
									gridDefinition={[{ colspan: 3 }, { colspan: 3 }, { colspan: 3 }, { colspan: 3 }]}
									children={
										<>
											<Select
												filteringType="auto"
												disabled={!this.props.game.loaded}
												placeholder="Status"
												selectedOption={this.props.game.currentOption.status}
												onChange={(option) => {
													option.detail.selectedOption.type !== this.props.game.currentStatus && this.props.setGame({ ...this.props.game, currentStatus: option.detail.selectedOption.type, currentOption: { ...this.props.game.currentOption, status: option.detail.selectedOption } });
													setTimeout(() => {
														this.loadGames();
													}, 0);
												}}
												options={this.props.config.game.options.status}
											/>
											<Select
												filteringType="auto"
												disabled={!this.props.game.loaded}
												placeholder="Category"
												selectedOption={this.props.game.currentOption.category}
												onChange={(option) => {
													option.detail.selectedOption.type !== this.props.game.currentCategory && this.props.setGame({ ...this.props.game, currentCategory: option.detail.selectedOption.type, currentOption: { ...this.props.game.currentOption, category: option.detail.selectedOption } });
													setTimeout(() => {
														this.loadGames();
													}, 0);
												}}
												options={this.props.config.game.options.category}
											/>
											<Select
												filteringType="auto"
												disabled={!this.props.game.loaded}
												placeholder="Provider"
												selectedOption={this.props.game.currentOption.provider}
												onChange={(option) => {
													option.detail.selectedOption.type !== this.props.game.currentProvider && this.props.setGame({ ...this.props.game, currentProvider: option.detail.selectedOption.type, currentOption: { ...this.props.game.currentOption, provider: option.detail.selectedOption } });
													setTimeout(() => {
														this.loadGames();
													}, 0);
												}}
												options={this.props.config.game.options.provider}
											/>
											<Input
												disabled={!this.props.game.loaded}
												onKeyDown={this.handleKeyPress}
												disableBrowserAutocorrect={true}
												placeholder="Game ID, name or something"
												inputMode="search"
												type="search"
												value={this.props.game.currentSearch}
												onChange={(input) => {
													this.props.setGame({ ...this.props.game, currentSearch: input.detail.value });
													clearTimeout(this.loadTimeout);
													this.loadTimeout = setTimeout(() => {
														this.loadGames();
													}, 1000);
													this.currentSearch = input.detail.value;
												}}
											/>
										</>
									}
								/>
							}
						/>
					}
					header={
						<Box
							margin={{ bottom: "m" }}
							children={
								<Header
									children="Games"
									counter={<>({this.props.game.data.length})</>}
									actions={
										canManageGames || canManageSectionGames ? (
											<SpaceBetween direction="horizontal" size="l">
												{canManageSectionGames ? (
													<Button variant="primary" disabled={this.props.game.loaded !== true || !availabilityReady} onClick={() => this.setState({ sectionGamesEditorOpen: true })}>
														Manage section games
													</Button>
												) : null}
												{canManageGames ? (
													<>
														<Button variant="primary" disabled={!this.props.game.loaded} onClick={() => this.props.setGamesMetaTagsModal((prev) => ({ ...prev, show: 1 }))}>
															Edit Tags
														</Button>
														<Button
															disabled={!this.props.game.loaded}
															className={this.props.styles["players-search-button"]}
															onClick={() => {
																this.props.setGamesAutomanageModal({ ...this.props.gamesAutoManageModal, show: 1 });
															}}
															variant="primary">
															Automanage
														</Button>
														<Button
															disabled={!this.props.game.loaded}
															className={this.props.styles["players-search-button"]}
															onClick={() => {
																this.props.setGamesAddModal({ ...this.props.gamesAddModal, show: 1 });
															}}
															variant="primary">
															Add game
														</Button>
														<Button
															className={this.props.styles["players-search-button"]}
															onClick={() => {
																this.props.setGamesSweepstakesModal((prev) => ({ ...prev, show: true }));
															}}
															variant="primary">
															Sweepstakes
														</Button>

														<Button
															iconName="settings"
															disabled={!this.props.game.loaded}
															className={[this.props.styles["players-search-button"], this.props.styles["btn"]].join(" ")}
															onClick={() => {
																this.props.setGamesAllEditModal({
																	...this.props.gamesAllEditModal,
																	values: {
																		active: null,
																		listed: null,
																		categoryID: null,
																	},
																	error: {
																		text: "",
																		code: 0,
																		timestamp: 0,
																	},
																	show: true,
																	loaded: true,
																});
															}}
															variant="primary"
															children="Edit"
														/>
													</>
												) : null}
											</SpaceBetween>
										) : (
											<></>
										)
									}
									description="Below listed games list"
								/>
							}
						/>
					}
					empty="No games found with requested filter"
				/>
			</>
		);
	}
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const user = useRecoilValue(userStore);
		const game = useRecoilValue(gameStore);
		const setGame = useSetRecoilState(gameStore);
		const config = useRecoilValue(configStore);
		const setConfig = useSetRecoilState(configStore);
		const gamesAddModal = useRecoilValue(gamesAddModalStore);
		const setGamesAddModal = useSetRecoilState(gamesAddModalStore);
		const gamesStatsModal = useRecoilValue(gamesStatsModalStore);
		const setGamesStatsModal = useSetRecoilState(gamesStatsModalStore);
		const gamesEditModal = useRecoilValue(gamesEditModalStore);
		const setGamesEditModal = useSetRecoilState(gamesEditModalStore);
		const gamesAutoManageModal = useRecoilValue(gamesAutoManageModalStore);
		const setGamesAutomanageModal = useSetRecoilState(gamesAutoManageModalStore);
		const gamesAllEditModal = useRecoilValue(gamesAllEditModalStore);
		const setGamesAllEditModal = useSetRecoilState(gamesAllEditModalStore);
		const setGamesMetaTagsModal = useSetRecoilState(gamesMetaTagsStore);
		const setGamesSweepstakesModal = useSetRecoilState(gamesSweepstakesModalStore);

		return (
			<Component
				{...props}
				setGamesAllEditModal={setGamesAllEditModal}
				gamesAllEditModal={gamesAllEditModal}
				user={user}
				game={game}
				setGame={setGame}
				config={config}
				setConfig={setConfig}
				gamesAddModal={gamesAddModal}
				setGamesAddModal={setGamesAddModal}
				gamesStatsModal={gamesStatsModal}
				setGamesStatsModal={setGamesStatsModal}
				gamesEditModal={gamesEditModal}
				setGamesEditModal={setGamesEditModal}
				setGamesAutomanageModal={setGamesAutomanageModal}
				gamesAutoManageModal={gamesAutoManageModal}
				setGamesMetaTagsModal={setGamesMetaTagsModal}
				setGamesSweepstakesModal={setGamesSweepstakesModal}
			/>
		);
	};
}

export default withRecoil(Games);
